import { ButtonHTMLAttributes, ReactNode } from 'react';
import { IconType } from 'react-icons';
import {
  actionBtnDanger,
  actionBtnGhost,
  actionBtnIconClass,
  actionBtnOutline,
  actionBtnPrimary,
  actionBtnSuccess,
} from '@/utils/actionButtonClasses';

type ActionButtonVariant = 'primary' | 'outline' | 'danger' | 'success' | 'ghost';

const variantClasses: Record<ActionButtonVariant, string> = {
  primary: actionBtnPrimary,
  outline: actionBtnOutline,
  danger: actionBtnDanger,
  success: actionBtnSuccess,
  ghost: actionBtnGhost,
};

type ActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ActionButtonVariant;
  icon?: IconType;
  iconRight?: IconType;
  isLoading?: boolean;
  children?: ReactNode;
};

/** Compact toolbar button — matches Backlog Create (icon + label, text-xs). */
export default function ActionButton({
  variant = 'primary',
  icon: Icon,
  iconRight: IconRight,
  isLoading = false,
  disabled,
  className = '',
  children,
  type = 'button',
  ...props
}: ActionButtonProps) {
  return (
    <button
      type={type}
      className={`${variantClasses[variant]} ${className}`.trim()}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <svg
          className={`${actionBtnIconClass} animate-spin`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : Icon ? (
        <Icon className={actionBtnIconClass} aria-hidden />
      ) : null}
      {children}
      {!isLoading && IconRight ? <IconRight className={actionBtnIconClass} aria-hidden /> : null}
    </button>
  );
}

/** Icon-only compact action (archive, delete, etc.). */
export function ActionIconButton({
  variant = 'outline',
  icon: Icon,
  className = '',
  ...props
}: Omit<ActionButtonProps, 'children' | 'iconRight'> & { icon: IconType }) {
  const iconVariant = variant === 'danger' ? 'danger' : variant === 'ghost' ? 'ghost' : 'outline';
  return (
    <button
      type="button"
      className={`${variantClasses[iconVariant]} !px-2 ${className}`.trim()}
      {...props}
    >
      <Icon className={actionBtnIconClass} aria-hidden />
    </button>
  );
}
